import React, { useState, useEffect } from 'react';
import { Check, Crown, Zap, Building2, Sparkles, Loader2, AlertCircle, LogIn, ArrowRight } from 'lucide-react';
import brandConfig from '../../config/brandConfig';

const getIcon = (planName) => {
  switch (planName) {
    case "free": return <Zap className="w-6 h-6" />;
    case "basic": return <Building2 className="w-6 h-6" />;
    case "premium": return <Crown className="w-6 h-6" />;
    case "enterprise": return <Sparkles className="w-6 h-6" />;
    default: return <Building2 className="w-6 h-6" />;
  }
};

const getColors = (planName, recommended) => {
  if (recommended) {
    return {
      bg: "bg-gradient-to-br from-orange-500 to-amber-500",
      border: "border-orange-400",
      iconBg: "bg-white/20",
      iconColor: "text-white",
      badgeBg: "bg-white",
      badgeText: "text-orange-600",
      isHighlight: true
    };
  }
  switch (planName) {
    case "free":
      return { bg: "bg-white dark:bg-gray-900", border: "border-gray-200 dark:border-gray-800", iconBg: "bg-gray-100 dark:bg-gray-800", iconColor: "text-gray-500 dark:text-gray-400" };
    case "premium":
      return { bg: "bg-white dark:bg-gray-900", border: "border-violet-200 dark:border-violet-800", iconBg: "bg-violet-100 dark:bg-violet-900/30", iconColor: "text-violet-600 dark:text-violet-400" };
    case "enterprise":
      return { bg: "bg-white dark:bg-gray-900", border: "border-amber-200 dark:border-amber-800", iconBg: "bg-amber-100 dark:bg-amber-900/30", iconColor: "text-amber-600 dark:text-amber-400" };
    default:
      return { bg: "bg-white dark:bg-gray-900", border: "border-gray-200 dark:border-gray-800", iconBg: "bg-gray-100 dark:bg-gray-800", iconColor: "text-gray-500 dark:text-gray-400" };
  }
};

const planToFeatures = (plan) => {
  const features = [
    { name: "Proyectos", limit: plan.max_projects ?? "Ilimitado" },
    { name: "Agentes", limit: plan.max_agents ?? "Ilimitado" },
    { name: "Clientes", limit: plan.max_clients ?? "Ilimitado" },
    { name: "Lotes", limit: plan.max_lots ?? "Ilimitado" },
    { name: "Cotizaciones", limit: "Ilimitado" },
    { name: "Ventas", limit: "Ilimitado" },
    { name: "Reservas", limit: "Ilimitado" },
    { name: "Almacenamiento", limit: plan.max_storage_gb ? `${plan.max_storage_gb} GB` : "Ilimitado" },
  ];
  if (plan.features?.cad_to_geojson_support) {
    features.push({ name: "Soporte CAD a GeoJSON de tus Lotes", limit: "Incluido" });
  }
  if (plan.features?.support_priority === "24/7") {
    features.push({ name: "Soporte 24/7", limit: "Incluido" });
  }
  return features;
};

const getAuthToken = () => {
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };
  return getCookie('token') || localStorage.getItem('token') || localStorage.getItem('access_token');
};

// Formatea el precio mostrado en la tarjeta
const priceLabel = (plan) => {
  if (plan.name === "free" || plan.price_monthly === 0) return { amount: "Gratis", period: "" };
  return { amount: `$${plan.price_monthly}`, period: "USD / mes" };
};

const SubscriptionSection = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasToken, setHasToken] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [subscribingId, setSubscribingId] = useState(null);
  const [notice, setNotice] = useState(null);

  const dashboardUrl = brandConfig.dashboardUrl;
  const apiUrl = brandConfig.apiUrl;

  useEffect(() => {
    // Mensaje de retorno desde PayPal (?subscription=success|cancelled)
    const params = new URLSearchParams(window.location.search);
    const sub = params.get('subscription');
    if (sub === 'cancelled') {
      localStorage.removeItem('lp_pending_plan');
      setNotice({ type: 'info', text: 'Suscripción cancelada. No se realizó ningún cobro.' });
    }

    const token = getAuthToken();
    setHasToken(!!token);

    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${apiUrl}/subscription-plans`);
        if (!response.ok) throw new Error('Error fetching plans');
        const data = await response.json();
        setPlans(data.data || []);
      } catch (err) {
        console.error('Error fetching subscription plans:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchCurrentPlan = async () => {
      try {
        const response = await fetch(`${apiUrl}/organizations/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const org = await response.json();
          setCurrentPlan(org.subscription_plan || 'free');
        }
      } catch (err) {
        console.error('Error fetching current plan:', err);
      }
    };

    // Tras volver de PayPal, el webhook activa el plan de forma asíncrona.
    // Consultamos varias veces hasta que el plan se refleje en la organización.
    const pollActivation = async (attempt = 0) => {
      const pending = localStorage.getItem('lp_pending_plan');
      try {
        const response = await fetch(`${apiUrl}/organizations/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const org = await response.json();
          const plan = org.subscription_plan || 'free';
          setCurrentPlan(plan);
          const activated = pending ? plan === pending : plan !== 'free';
          if (activated) {
            localStorage.removeItem('lp_pending_plan');
            setNotice({ type: 'success', text: '¡Listo! Tu plan ya está activo.' });
            return;
          }
        }
      } catch (err) {
        console.error('Error verificando activación:', err);
      }
      if (attempt < 8) {
        setTimeout(() => pollActivation(attempt + 1), 3000);
      } else {
        localStorage.removeItem('lp_pending_plan');
        setNotice({ type: 'success', text: 'Pago recibido. Tu plan se activará en breve; recarga la página en unos minutos si no lo ves.' });
      }
    };

    fetchPlans();
    if (sub === 'success' && token) {
      // Mostramos "Activando..." unos segundos antes del primer chequeo,
      // para que el usuario alcance a verlo aunque el webhook active rápido.
      setNotice({ type: 'loading', text: 'Estamos activando tu plan...' });
      setTimeout(() => pollActivation(), 2000);
    } else if (token) {
      fetchCurrentPlan();
    }
  }, [apiUrl]);

  // Inicia la suscripción de PayPal y redirige al checkout
  const handleSubscribe = async (plan) => {
    const token = getAuthToken();
    if (!token) {
      window.location.href = `${dashboardUrl}/iniciar-sesion`;
      return;
    }
    setSubscribingId(plan.id);
    setNotice(null);
    try {
      const orgResponse = await fetch(`${apiUrl}/organizations/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!orgResponse.ok) {
        window.location.href = `${dashboardUrl}/iniciar-sesion`;
        return;
      }
      const org = await orgResponse.json();
      const returnUrl = `${window.location.origin}/planes?subscription=success`;
      const cancelUrl = `${window.location.origin}/planes?subscription=cancelled`;
      const res = await fetch(`${apiUrl}/subscriptions/${org.id}/paypal/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ new_plan_id: plan.id, return_url: returnUrl, cancel_url: cancelUrl }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setNotice({ type: 'error', text: err.detail || 'No se pudo iniciar la suscripción.' });
        setSubscribingId(null);
        return;
      }
      const data = await res.json();
      if (data.approve_url) {
        // Guardamos el plan elegido para confirmar la activación al volver
        localStorage.setItem('lp_pending_plan', plan.name);
        window.location.href = data.approve_url;
      } else {
        setNotice({ type: 'error', text: 'PayPal no devolvió un link de pago.' });
        setSubscribingId(null);
      }
    } catch (err) {
      console.error('Error subscribing to plan:', err);
      setNotice({ type: 'error', text: 'Error al conectar con el servidor.' });
      setSubscribingId(null);
    }
  };

  const handleContact = (plan) => {
    const whatsappNumber = brandConfig.whatsappSupport.replace(/\+/g, '');
    const message = `Hola, estoy interesado en el plan ${plan.display_name} de ${brandConfig.name}. ¿Podrían ayudarme?`;
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="py-24 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 flex justify-center items-center">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: brandConfig.primaryColor }} />
          <span className="ml-3 text-gray-500 dark:text-gray-400 font-medium">Cargando planes...</span>
        </div>
      </div>
    );
  }

  if (error || plans.length === 0) {
    return (
      <div className="py-24 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-2">No se pudieron cargar los planes</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">Por favor, intenta de nuevo más tarde.</p>
          <a
            href={`https://wa.me/${brandConfig.whatsappSupport.replace(/\+/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-colors"
          >
            Hablar con un asesor
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="py-24 lg:py-32 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <h2 className="text-4xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6 tracking-tight">
            Planes de
            <span className="font-display italic" style={{ color: brandConfig.primaryColor }}> Suscripción</span>
          </h2>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto">
            Elige el plan que mejor se adapte a tu negocio. Precios en USD, facturación mensual. Cancela cuando quieras.
          </p>
        </div>

        {/* Aviso (retorno de PayPal o error) */}
        {notice && (
          <div
            className={`mb-10 max-w-2xl mx-auto rounded-2xl border px-6 py-6 flex items-center justify-center gap-3 text-center ${
              notice.type === 'success' || notice.type === 'loading'
                ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200'
                : notice.type === 'error'
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300'
                  : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            {notice.type === 'loading' && <Loader2 className="w-6 h-6 animate-spin flex-shrink-0" />}
            {notice.type === 'success' && <Check className="w-7 h-7 flex-shrink-0" />}
            <span className="text-xl font-bold">{notice.text}</span>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-12">
          {plans.map((plan) => {
            const isRecommended = plan.features?.recommended;
            const isCurrentUserPlan = currentPlan && currentPlan === plan.name;
            const colors = getColors(plan.name, isRecommended);
            const features = planToFeatures(plan);
            const price = priceLabel(plan);
            // Pagable por PayPal = el plan tiene un billing plan configurado
            const isPaid = !!plan.paypal_plan_id;
            const isSubscribing = subscribingId === plan.id;

            return (
              <div
                key={plan.name}
                className={`relative rounded-3xl p-7 transition-all duration-300 card-hover ${
                  isCurrentUserPlan
                    ? "bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/10 border-2 border-emerald-400 dark:border-emerald-600 ring-2 ring-emerald-200 dark:ring-emerald-800"
                    : colors.isHighlight
                      ? `${colors.bg} border-2 ${colors.border} text-white shadow-xl shadow-orange-500/20`
                      : `${colors.bg} ${colors.border} border`
                }`}
              >
                {isCurrentUserPlan && (
                  <div className="absolute -top-3 left-4">
                    <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">Tu Plan Actual</span>
                  </div>
                )}
                {isRecommended && !isCurrentUserPlan && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className={`${colors.badgeBg} ${colors.badgeText} text-xs font-bold px-4 py-1.5 rounded-full shadow-md`}>Recomendado</span>
                  </div>
                )}

                <div className={`${colors.iconBg} ${colors.iconColor} w-12 h-12 rounded-xl flex items-center justify-center mb-5`}>
                  {getIcon(plan.name)}
                </div>

                <h3 className={`text-xl font-extrabold mb-2 tracking-tight ${colors.isHighlight && !isCurrentUserPlan ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                  {plan.display_name}
                </h3>

                <div className="mb-5 flex items-baseline gap-1.5">
                  <span className={`text-3xl font-extrabold ${colors.isHighlight && !isCurrentUserPlan ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                    {price.amount}
                  </span>
                  {price.period && (
                    <span className={`text-sm font-medium ${colors.isHighlight && !isCurrentUserPlan ? 'text-white/70' : 'text-gray-400 dark:text-gray-500'}`}>
                      {price.period}
                    </span>
                  )}
                </div>

                <ul className="space-y-2.5 mb-6">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${colors.isHighlight && !isCurrentUserPlan ? 'text-white/80' : 'text-emerald-500'}`} />
                      <span className={colors.isHighlight && !isCurrentUserPlan ? 'text-white/90' : 'text-gray-600 dark:text-gray-400'}>
                        <span className="font-bold">{feature.name}:</span> <span className="opacity-70">{feature.limit}</span>
                      </span>
                    </li>
                  ))}
                </ul>

                {(() => {
                  // No logueado
                  if (!hasToken) {
                    return (
                      <button
                        onClick={() => window.location.href = `${dashboardUrl}/iniciar-sesion`}
                        className={`w-full py-3 px-4 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer text-sm ${
                          colors.isHighlight
                            ? "bg-white text-orange-600 hover:bg-orange-50 shadow-md"
                            : "bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:opacity-90 shadow-md"
                        }`}
                      >
                        <LogIn className="w-4 h-4" />
                        Iniciar Sesión
                      </button>
                    );
                  }

                  // Plan actual
                  if (isCurrentUserPlan) {
                    return (
                      <button disabled className="w-full py-3 px-4 rounded-xl font-bold text-sm bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 cursor-not-allowed">
                        Tu Plan Actual
                      </button>
                    );
                  }

                  // Pagable por PayPal -> suscribir
                  if (isPaid) {
                    return (
                      <button
                        onClick={() => handleSubscribe(plan)}
                        disabled={isSubscribing}
                        className={`w-full py-3 px-4 rounded-xl font-bold text-sm transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed ${
                          colors.isHighlight
                            ? "bg-white text-orange-600 hover:bg-orange-50 shadow-md"
                            : "bg-emerald-600 text-white hover:bg-emerald-700 shadow-md"
                        }`}
                      >
                        {isSubscribing ? (
                          <><Loader2 className="w-4 h-4 animate-spin" />Redirigiendo...</>
                        ) : (
                          <>Suscribirse<ArrowRight className="w-4 h-4" /></>
                        )}
                      </button>
                    );
                  }

                  // Free (plan base) -> sin acción de compra
                  if (plan.name === "free" || plan.price_monthly === 0) {
                    return (
                      <button disabled className="w-full py-3 px-4 rounded-xl font-bold text-sm bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed">
                        Gratis
                      </button>
                    );
                  }

                  // Plan con precio pero sin PayPal configurado -> contactar
                  return (
                    <button
                      onClick={() => handleContact(plan)}
                      className={`w-full py-3 px-4 rounded-xl font-bold text-sm transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer ${
                        colors.isHighlight
                          ? "bg-white text-orange-600 hover:bg-orange-50 shadow-md"
                          : "bg-emerald-600 text-white hover:bg-emerald-700 shadow-md"
                      }`}
                    >
                      Hablar con un asesor
                    </button>
                  );
                })()}
              </div>
            );
          })}
        </div>

        {/* Info Footer */}
        <div className="p-5 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-2xl">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Los pagos se procesan de forma segura con <span className="font-bold">PayPal</span>. La suscripción se renueva
            cada mes y puedes cancelarla cuando quieras desde tu cuenta.
          </p>
        </div>

        {/* CAD Service */}
        <div className="mt-8 relative rounded-3xl p-8 overflow-hidden text-white grain-overlay" style={{
          background: 'linear-gradient(135deg, #102a43 0%, #243b53 50%, #102a43 100%)'
        }}>
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `radial-gradient(circle at 80% 50%, ${brandConfig.primaryColor}40 0%, transparent 50%)`
          }} />
          <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0 w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
              <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
              </svg>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-lg font-extrabold text-white mb-1 tracking-tight">
                Servicio de conversión CAD a GeoJSON
              </h3>
              <p className="text-white/50 text-sm">
                ¿Tienes tus lotes en archivos CAD (DWG, DXF)? Nosotros los convertimos a GeoJSON listo para usar.
              </p>
            </div>
            <button
              onClick={() => {
                const whatsappNumber = brandConfig.whatsappSupport.replace(/\+/g, '');
                const message = `Hola, me interesa el servicio de conversión de archivos CAD a GeoJSON para mis lotes en ${brandConfig.name}.`;
                window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
              }}
              className="flex-shrink-0 px-6 py-3 bg-white text-gray-900 font-bold rounded-xl hover:bg-gray-100 transition-colors shadow-lg text-sm"
            >
              Solicitar servicio
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSection;
